// NationalPartyShareBar.jsx
import { useState, useMemo } from "react";  // ← add useMemo
import { Box, ToggleButton, ToggleButtonGroup ,Typography} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
  LineChart,
  Line,
  CartesianGrid,
  Tooltip,
  Legend,
  
} from "recharts";
import {  ButtonBase } from "@mui/material";
import { motion, LayoutGroup } from "framer-motion";
import { PARTY_COLOURS_BY_NAME as PARTY_COLOURS, PARTY_FIELDS_BY_NAME as PARTY_FIELDS } from "./constants/partyConfig";

const SERIES = ["TPM", "Green", "Labour", "NZF", "ACT", "National"];
const TOTAL_SEATS = 120;

// ── Stacked bar label ────────────────────────────────────────────────
function SegmentLabel({ x, y, width, height, value, name }) {
  if (width < 20) return null;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const onlyPct = width < 10;

  return (
    <g style={{ pointerEvents: "none" }}>
      {!onlyPct && (
        <text
          x={cx} y={cy - 6}
          textAnchor="middle" dominantBaseline="central"
          fill="#fff" fontWeight="700" fontSize="10"
        >
          {name}
        </text>
      )}
      <text
        x={cx} y={onlyPct ? cy : cy + 8}
        textAnchor="middle" dominantBaseline="central"
        fill="#fff" fontWeight="600" fontSize="10"
      >
        {`${Math.round(value)}%`}
      </text>
    </g>
  );
}

// ── Custom tooltip for the line chart ────────────────────────────────
function WaveTooltip({ active, payload, label, mode }) {
  if (!active || !payload?.length) return null;
  const suffix = mode === "seats" ? " seats" : "%";
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        px: 1.5,
        py: 1,
        fontSize: 12,
        boxShadow: 2,
      }}
    >
      <Box sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Box>
      {payload
        .sort((a, b) => b.value - a.value)
        .map((entry) => (
          <Box key={entry.name} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: entry.color,
                flexShrink: 0,
              }}
            />
            <span>
              {entry.name}: <strong>{mode === "seats" ? entry.value : entry.value.toFixed(1) + suffix}</strong>
            </span>
          </Box>
        ))}
    </Box>
  );
}

// ── Main component ───────────────────────────────────────────────────
export default function VoteLineCharts({
  metricRowsConst,
  metricRowsNat,
  selectedFeature,
  WIDTH,
}) {
  const toPct = (v) => {
    if (v == null || Number.isNaN(v)) return 0;
    return Number(v);
  };
const [mode, setMode] = useState("share");

  // ── Pick data source (no early returns before hooks) ───────────────
  const data_for_chart = useMemo(() => {
    if (!metricRowsNat) return null;
    if (selectedFeature && !metricRowsConst) return null;

    if (selectedFeature && metricRowsConst && mode === "share") {
      return metricRowsConst.filter(
        (g) => Number(g.area_id) === Number(selectedFeature.area_id)
      );
    }
    return metricRowsNat;
  }, [metricRowsNat, metricRowsConst, selectedFeature, mode]);

  const lineData = useMemo(() => {
    if (!data_for_chart) return [];
    return data_for_chart
      .slice()
      .sort((a, b) => Number(a.wave) - Number(b.wave))
      .map((waveRow) => {
         const rawShares = {};
      SERIES.forEach((key) => {
        rawShares[key] = toPct(waveRow[PARTY_FIELDS[key]]);
      });

      const totalShare = Object.values(rawShares).reduce((s, v) => s + v, 0);

      const point = { wave: waveRow.wave_date || `Wave ${waveRow.wave}` };

      SERIES.forEach((key) => {
        if (mode === "seats") {
          point[key] = Math.round((rawShares[key] / totalShare) * TOTAL_SEATS);
        } else {
          point[key] = rawShares[key];
        }
      });

      return point;
      });
  }, [data_for_chart, mode]);

  // ── Now safe to bail out ───────────────────────────────────────────
  if (!data_for_chart || lineData.length === 0) return null;



  // ── Render ─────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: WIDTH,
        gap: 2,
      }}
    >

      {/* Toggle: Vote Share ↔ Implied Seats */}
     <Box
  sx={{
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    p: 0,
    borderRadius: 99,
    bgcolor: "rgba(0,0,0,0.04)",
    mt: 8,
    
  }}
>
  <LayoutGroup id="mode-toggle-group">
    {[
      { value: "share", label: "Vote Share %" },
      { value: "seats", label: "Implied Seats" },
    ].map(({ value, label }) => {
      const isSelected = mode === value;

      return (
        <ButtonBase
          key={value}
          disableRipple
          onClick={() => setMode(value)}
          sx={{
            position: "relative",
            px: 2.5,
            py: 0.4,
            borderRadius: 99,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: isSelected ? "#1976D2" : "#BDBDBD",
            transition: "color 0.2s ease",
            "&:hover": {
              color: isSelected ? "#BDBDBD" : "#1976D2",
              backgroundColor: !isSelected ? "rgba(0,0,0,0.04)" : "transparent",
            },
            zIndex: 1,
          }}
        >
          <span style={{ position: "relative", zIndex: 2 }}>{label}</span>

          {isSelected && (
            <Box
              component={motion.div}
              layoutId="mode-active-pill"
              transition={{ type: "spring", stiffness: 500, damping: 35 }}
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: 99,
                bgcolor: "white",
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                border: "1px solid rgba(0,0,0,0.02)",
                zIndex: 1,
              }}
            />
          )}
        </ButtonBase>
      );
    })}
  </LayoutGroup>
</Box>
       {selectedFeature && mode === "share"&&(
          <Typography sx={{mt:0.5}}>
           <b> Vote shares in</b>: {selectedFeature?.area_name}
          </Typography> 
          )}

      {/* Wave comparison line chart */}
      {lineData.length > 0 && (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={lineData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="wave"
              tick={{ fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              padding={{ left: 20, right: 20 }}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              domain={mode === "seats" ? [0, "auto"] : [0, "auto"]}
              tickFormatter={(v) => (mode === "seats" ? v : `${v}%`)}
              width={40}
            />
            <Tooltip content={<WaveTooltip mode={mode} />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
            />
            {SERIES.map((key) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={PARTY_COLOURS[key]}
                strokeWidth={2.5}
                dot={{ r: 4, fill: PARTY_COLOURS[key], strokeWidth: 0 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
}